/**
 * 마지막 주고받기 바꿔 보내기 — 답변 '다시 만들기'(regenerate)와 내 메시지 '고쳐 보내기'(edit_resend).
 *
 * 바꾼 턴은 새 턴 id 로 다시 만들고, 저장할 때 옛 사용자·답변 문서를 같은 배치에서 지운다.
 * 서버(backend services/chat_turn_replace.py)는 replace_message_ids 로 받은 옛 문서를 모델 기록에서 빼고 잇는다.
 * 끝까지 만들지 못하면(오류·받은 글 없이 멈춤) 화면은 옛 턴으로 돌아가고 Firestore 도 그대로다.
 */
import { parseClarifyAnswerMessage } from '../components/chat/ClarifyCard';

export const TURN_ACTION_REGENERATE = 'regenerate';
export const TURN_ACTION_EDIT_RESEND = 'edit_resend';

export const isChatRegenEditEnabled = () => (
  !['false', '0', 'off'].includes(String(process.env.REACT_APP_CHAT_REGEN_EDIT || '').trim().toLowerCase())
);

// 파일 편집·발표 자료·학습지·이미지 작업은 답과 함께 파일·작업이 생긴다. 같은 요청을 다시 보내면 두 번 고친다.
const BLOCKED_CATEGORIES = new Set(['document', 'ppt_maker', 'worksheet_gen', 'image_generation']);
const LOCAL_ONLY_ID_RE = /^(?:user|ai)_\d+$/;
const TURN_AI_DOC_ID_RE = /^turn_([A-Za-z0-9_-]{8,64})_ai$/;

const hasItems = (value) => Array.isArray(value) && value.length > 0;

// 파일 만들기 제안(artifact_proposal)은 명시 요청이면 바로 만들기가 시작된다 — 다시 보내면 파일이 한 벌 더 생긴다.
const proposedArtifact = (message) => (
  Array.isArray(message.suggestions) && message.suggestions.some((item) => item?.type === 'artifact_proposal')
);

const answerMadeSideEffects = (message) => Boolean(
  hasItems(message.artifacts)
  || proposedArtifact(message)
  || message.pendingArtifact
  || message.hasImage
  || message.inline_image
  || message.clarify_card
  || message.followup_form
);

/** 화면 목록에서 바꿔 보낼 수 있는 마지막 턴. 없으면 null. */
export const findReplaceableTurn = (messages, { category = 'general' } = {}) => {
  const list = Array.isArray(messages) ? messages : [];
  if (BLOCKED_CATEGORIES.has(category) || list.length < 2) return null;
  const aiIndex = list.length - 1;
  const ai = list[aiIndex];
  const user = list[aiIndex - 1];
  if (ai?.role !== 'assistant' || user?.role !== 'user') return null;
  if (ai.streaming || ai.error || ai.saveFailedIn || user.saveFailedIn) return null;
  if (BLOCKED_CATEGORIES.has(ai.category)) return null;
  if (answerMadeSideEffects(ai)) return null;
  const text = String(user.content || '').trim();
  if (!text || parseClarifyAnswerMessage(text)) return null;
  return { user, ai, userIndex: aiIndex - 1, aiIndex, text };
};

/**
 * 지울 옛 메시지 문서 id [사용자, 답변]. 로그인 턴은 turn_{id}_user / turn_{id}_ai 로 저장된다.
 * 방금 끝난 턴의 사용자 말풍선은 아직 임시 id(user_123)라 답변 문서 id 에서 짝을 찾는다.
 * 저장 문서를 확정할 수 없으면 null — 바꿔 보내면 옛 턴이 Firestore 에 남아 두 벌이 된다.
 */
export const replaceDocIdsFor = ({ user, ai } = {}) => {
  const aiId = String(ai?.id || '');
  if (!aiId || LOCAL_ONLY_ID_RE.test(aiId)) return null;
  const match = aiId.match(TURN_AI_DOC_ID_RE);
  const userId = match ? `turn_${match[1]}_user` : String(user?.id || '');
  if (!userId || LOCAL_ONLY_ID_RE.test(userId)) return null;
  return [userId, aiId];
};

/** 옛 턴 자리를 새 말풍선 두 개로 바꾼다(같은 자리, 중복 없음). */
export const swapReplacedTurn = (messages, replace, userMessage, aiMessage) => {
  const oldIds = new Set([replace?.user?.id, replace?.ai?.id].filter(Boolean));
  const kept = messages.filter((message) => !oldIds.has(message.id));
  return [...kept, userMessage, aiMessage];
};

/** 새 턴을 빼고 옛 턴을 되돌린다(오류·받은 글 없이 멈춤). */
export const restoreReplacedTurn = (messages, replace, newIds = []) => {
  const drop = new Set([...newIds, replace?.user?.id, replace?.ai?.id].filter(Boolean));
  const kept = messages.filter((message) => !drop.has(message.id));
  return [...kept, replace.user, replace.ai].filter(Boolean);
};

/** 바꿀 턴을 뺀 목록(익명 대화 기록·로컬 저장 기준). */
export const withoutReplacedTurn = (messages, replace) => {
  const oldIds = new Set([replace?.user?.id, replace?.ai?.id].filter(Boolean));
  return (Array.isArray(messages) ? messages : []).filter((message) => !oldIds.has(message.id));
};

/** 이번 요청에 실을 '새 첨부' — 옛 턴에서 새로 붙였던 파일 중 지금도 요청에 실리는 것만. */
export const newlyAttachedForReplace = (oldUserMessage, requestFileIds) => {
  const requested = new Set(Array.isArray(requestFileIds) ? requestFileIds : []);
  const ids = (Array.isArray(oldUserMessage?.newlyAttachedFileIds) ? oldUserMessage.newlyAttachedFileIds : [])
    .filter((id) => requested.has(id));
  return ids.length > 0 ? ids : null;
};

export const turnReplaceFailedNotice = (action) => (
  action === TURN_ACTION_EDIT_RESEND
    ? '고쳐 보내지 못했어요. 잠시 후 다시 해 주세요.'
    : '다시 만들지 못했어요. 잠시 후 다시 해 주세요.'
);
